import { useQueryClient } from '@tanstack/react-query';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { Loading } from 'components/uikit/suspense/Loading';
import { useState } from 'react';
import { QueryFactory } from 'services/api';

import styles from './RefreshVariableModal.module.scss';
import { Trans } from 'react-i18next';
import { VariableDocumentList } from '../variableDocumentsList/VariableDocumentsList';
import { useHandleErrorOnFetchVariable } from '../useHandleErrorOnFetchVariable.ts';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import { useVariablesContext } from '../../variablesContext/VariablesContext.tsx';

type RefreshVariableModalProps = {
  variableId: number;
  variableName: string;
  projectId: number;
  revisionId: number;
  onVariableUpdate?: (variableId: number) => Promise<void>;
  onClose: () => void;
};

export const RefreshVariableModal: React.FC<RefreshVariableModalProps> = (
  props,
) => {
  const buttonTitle = useScopedTranslation('Button.Title');
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const { refreshVariables } = useVariablesContext();

  const variableQuery = QueryFactory.VariableQuery.useGetQuery(
    props.variableId,
    props.revisionId,
    null,
    { enabled: !!props.variableId },
  );

  const handleErrorOnFetchVariable = useHandleErrorOnFetchVariable();

  return (
    <div>
      <Loading loading={isLoading}>
        <p className={styles.refreshMessage}>
          <Trans
            i18nKey="Component.UpdateVariableModal.message"
            components={{
              red: <span className={styles.danger} />,
            }}
          />
        </p>
        {variableQuery.data?.documents &&
          variableQuery.data.documents.length > 0 && (
            <>
              <p className={styles.refreshMessage}>
                <Trans
                  i18nKey="Component.UpdateVariableModal.updating_documents"
                  values={{ variableName: props.variableName }}
                />
              </p>
              <VariableDocumentList documents={variableQuery.data.documents} />
            </>
          )}
      </Loading>
      <div className={styles.footer}>
        <Button
          className={styles.button}
          title={buttonTitle.t('cancel')}
          color={ButtonColor.Secondary}
          disabled={isLoading}
          onClick={() => props.onClose()}
        />
        <Button
          title={buttonTitle.t('update')}
          className={styles.button}
          color={ButtonColor.DestroySecondary}
          disabled={isLoading}
          onClick={async () => {
            try {
              setIsLoading(true);
              await props.onVariableUpdate?.(props.variableId);

              await Promise.all([
                refreshVariables(),
                queryClient.invalidateQueries({
                  queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
                }),
                variableQuery.data?.documents?.forEach((doc) =>
                  queryClient.invalidateQueries({
                    queryKey: QueryFactory.DocumentQuery.getQueryKey(doc.id),
                  }),
                ),
              ]);
            } catch (e: any) {
              showErrorToast(e);

              await handleErrorOnFetchVariable({
                error: e,
                documentsIds:
                  variableQuery.data?.documents?.map((d) => d.id) ?? [],
                revisionId: props.revisionId,
                onVariableUpdate: async () => {
                  await props.onVariableUpdate?.(props.variableId);
                },
              });
            } finally {
              setIsLoading(false);
              props.onClose();
            }
          }}
        />
      </div>
    </div>
  );
};
